import { FC, memo } from 'react';
import {
  Modal,
  Box,
  Typography,
  Button,
  Backdrop,
  Fade,
  Divider,
} from '@mui/material';
import { X } from '@phosphor-icons/react';

interface ReusableModalProps {
  open: boolean;
  onClose: () => void;
  title?: string | React.ReactNode;
  children: React.ReactNode;
  subtitle?: string;
  width?: number | string;
  backgroundColor?: string;
  padding?: string;
  closeOnBackdropClick?: boolean;
  headerRight?: React.ReactNode;
}

const modalStyles = {
  wrapper: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    bgcolor: 'background.paper',
    borderRadius: '8px',
    boxShadow: 26,
    overflowY: 'auto',
    height: 'fit-content',
    maxHeight: '95vh',
  },
  closeButton: {
    position: 'absolute',
    right: 8,
    top: 8,
    minWidth: 'auto',
    p: 1,
  },
  title: {
    fontSize: '16px',
    fontWeight: 500,
    fontFamily: 'DM Sans',
    lineHeight: '100%',
    letterSpacing: '-1%',
    textAlign: 'center',
    color: 'custom.textBlue',
  },
  subtitle: {
    fontSize: '14px',
    color: 'lightGray.text',
  },
};

const ReusableModal: FC<ReusableModalProps> = memo(
  ({
    open,
    onClose,
    title,
    subtitle,
    children,
    width = 400,
    backgroundColor,
    padding = '18px',
    closeOnBackdropClick = false,
    headerRight,
  }) => {
    const handleClose = (reason: string) => {
      if (reason === 'backdropClick' && !closeOnBackdropClick) {
        return;
      }
      onClose();
    };

    return (
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby={title ? 'modal-title' : undefined}
        aria-describedby="modal-description"
        closeAfterTransition
        disableEscapeKeyDown
        disableAutoFocus
        slots={{ backdrop: Backdrop }}
        slotProps={{
          backdrop: {
            timeout: 500,
          },
        }}
        sx={{
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.4)',
            backdropFilter: 'blur(4px)',
          },
        }}
      >
        <Fade in={open}>
          <Box
            sx={{
              ...modalStyles.wrapper,
              width,
              padding,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: backgroundColor || 'background.paper',
                mx: -2.25,
                mt: -2.25,
                p: 2.25,
                borderTopLeftRadius: '8px',
                borderTopRightRadius: '8px',
                position: 'relative',
              }}
            >
              <Button
                onClick={onClose}
                sx={modalStyles.closeButton}
                aria-label="Close modal"
              >
                <X size={16} color="lightGray.text" />
              </Button>
              {headerRight && (
                <Box
                  sx={{
                    position: 'absolute',
                    right: 44,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  {headerRight}
                </Box>
              )}
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                {title && (
                  <Typography
                    id="modal-title"
                    variant="h6"
                    component="h2"
                    mb={0}
                    sx={modalStyles.title}
                  >
                    {title}
                  </Typography>
                )}
                {subtitle && (
                  <Typography id="modal-subtitle" sx={modalStyles.subtitle}>
                    {subtitle}
                  </Typography>
                )}
              </Box>
            </Box>
            <Divider sx={{ mx: -2.25 }} />
            <Box id="modal-description" sx={{ mt: 2 }}>
              {children}
            </Box>
          </Box>
        </Fade>
      </Modal>
    );
  }
);

ReusableModal.displayName = 'ReusableModal';

export default ReusableModal;
